import { useCallback } from 'react';
import { saveAs } from 'file-saver';
import { pdf } from '@react-pdf/renderer';
import { useMonkAppState } from '@monkvision/common';
import { useMonkApi } from '@monkvision/network';
import { CaptureWorkflow } from '@monkvision/types';
import { TeslaPdf } from '../../components/TeslaPdf';
import { generateTeslaPdfInspectionData } from './generatePdfInspectionData';
import { getInspectionDisplayLocales } from '../../utils';

export interface UseTeslaPdfGeneratorParams {
  openInNewTab: boolean;
}

export function useTeslaPdfGenerator({ openInNewTab }: UseTeslaPdfGeneratorParams) {
  const { config, authToken, getCurrentSights } = useMonkAppState({
    requireWorkflow: CaptureWorkflow.PHOTO,
  });
  const { getInspection, uploadPdf } = useMonkApi({
    authToken: authToken ?? '',
    apiDomain: config.apiDomain,
    thumbnailDomain: config.thumbnailDomain,
  });

  const generateAndDownloadPdf = useCallback(
    async (id: string) => {
      const { entities } = await getInspection({ id, light: false });
      const inspection = entities.inspections.find((i) => i.id === id);
      if (!inspection) {
        throw new Error('Inspection in PDF is not defined.');
      }
      const data = await generateTeslaPdfInspectionData(inspection, entities, getCurrentSights());
      const { lang, currency, country } = getInspectionDisplayLocales(inspection);
      const pdfDocument = pdf(
        <TeslaPdf data={data} lang={lang} currency={currency} country={country} />,
      );
      const blob = await pdfDocument.toBlob();
      if (openInNewTab) {
        window.open(URL.createObjectURL(blob));
      } else {
        const fileName = `condition-report-${id}.pdf`;
        saveAs(blob, fileName);
      }
      await uploadPdf({ id, pdf: blob, timeout: 60000 });
    },
    [getInspection, openInNewTab],
  );

  return { generateAndDownloadPdf };
}
